// library imports
import { Empty, Tree, message } from "antd";
import { useEffect, useState, useContext } from "react";
import { Line } from "@ant-design/plots";
import dayjs from "dayjs";
// component imports
import Analytics from "../../components/analytics/Analytics";
import { VerticalDateFilter } from "../../components/common/DateFilter";
// context imports
import { IndexContext } from "../../context/IndexContext";
// hook imports
import useFetch from "../../hooks/useFetch";

function DeviceParameterFilter({ treeData, checkedKeys, setCheckedKeys }) {
  const [expandedKeys, setExpandedKeys] = useState([]);
  const onExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
  };

  const onCheck = (checkedKeysValue) => {
    const filteredData = checkedKeysValue.filter((item) => item.includes(":"));
    setCheckedKeys(filteredData);
  };

  return (
    <Tree
      checkable
      onExpand={onExpand}
      expandedKeys={expandedKeys}
      autoExpandParent={true}
      onCheck={onCheck}
      checkedKeys={checkedKeys}
      treeData={treeData}
      selectable={false}
      height={"calc(100vh - 600px)"}
      style={{ overflowY: "scroll" }}
    />
  );
}
export default function ParameterTrendingPage() {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  const [isParameterDataLoading, setIsParameterDataLoading] = useState(false);
  const [isTrendingDataLoading, setIsTrendingDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [startDateSelection, setStartDateSelection] = useState(
    dayjs().subtract(6, "day")
  );
  const [endDateSelection, setEndDateSelection] = useState(dayjs());

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  const [parameterTrendingData, setParameterTrendingData] = useState([]);
  const [trendingDownloadData, setTrendingDownloadData] = useState([]);

  const [treeData, setTreeData] = useState([]);
  const [
    deviceParameterFiltercheckedKeys, // the checked key will be in the format <DeviceKey>:<ParameterKey>
    setDeviceParameterFilterCheckedKeys,
  ] = useState([]);

  const onFilterClick = () => {
    if (startDateSelection > endDateSelection) {
      message.error("End date must be later than or same as start date.");
    } else if (endDateSelection.diff(startDateSelection, "day") > 31) {
      message.warning("Date range cannot exceed 31 days.");
    } else {
      getTrendingData();
    }
  };

  const onExportClick = () => {
    if (trendingDownloadData.length < 1) {
      message.error("No data to export");
    } else {
      downloadTrendingData();
    }
  };

  const lineChartConfig = {
    autoFit: true,
    data: parameterTrendingData,
    xField: "date",
    yField: "value",
    seriesField: "deviceParameter",
    legend: {
      layout: "horizontal",
      position: "bottom",
    },
    point: false,
    xAxis: {
      tickCount: 10,
      title: {
        text: "Date",
        style: {
          fontSize: 16,
        },
      },
    },
    yAxis: {
      title: {
        text: "Value",
        style: {
          fontSize: 16,
        },
      },
    },
    meta: {
      date: {
        type: "time",
        mask: "YYYY-MM-DD HH:mm:ss",
      },
      value: {
        formatter: (value) =>
          value
            ? value.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : value === 0
            ? value.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : value,
      },
    },
  };

  /** API to get the device parameter */
  function getDeviceParameter() {
    setIsParameterDataLoading(true);
    fetchWrapper({
      endpoint_url: "parameter_trending/getDeviceParameter",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setTreeData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device parameter.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setIsParameterDataLoading(false);
      },
    });
  }

  /** API to get the trending data */
  function getTrendingData() {
    setIsTrendingDataLoading(true);
    fetchWrapper({
      endpoint_url: "parameter_trending/getTrendingData",
      method: "GET",
      set_content_type_header: false,
      params: {
        table_names: deviceParameterFiltercheckedKeys,
        start_date: startDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDateSelection.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        site_id: siteSelected,
      },
      onSuccess: (response) => {
        if (response.data === false) {
          notificationApi.error({
            message: response.message,
          });
        } else {
          setParameterTrendingData(response.data.graph_data);
          setTrendingDownloadData(response.data.download_data);
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get parameter trending data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setIsTrendingDataLoading(false);
      },
    });
  }

  /** API to download parameter trending data */
  function downloadTrendingData() {
    setDownloadLoading(true);

    fetchWrapper({
      endpoint_url: "parameter_trending/downloadParameterTrendingData",
      method: "POST",
      json_output: false,
      body: {
        data: trendingDownloadData,
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Parameter_Data_${dayjs().format(
              "YYYYMMDD_HHmmss"
            )}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setDownloadLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  useEffect(() => {
    setParameterTrendingData([]);
    setDeviceParameterFilterCheckedKeys([]);
    if (siteSelected) {
      getDeviceParameter();
    }
  }, [siteSelected]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  // when site commissioning date changes, need to update start date
  useEffect(() => {
    if (siteCommissioningDate) {
      setStartDateSelection(
        dayjs(
          Math.max(
            dayjs().subtract(6, "day").valueOf(),
            dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
          )
        )
      );
    }
  }, [siteCommissioningDate]);

  return (
    <Analytics
      title={"Trending"}
      contentComponent={
        parameterTrendingData.length > 0 ? (
          <Line {...lineChartConfig} />
        ) : (
          <Empty />
        )
      }
      isTrendingDataLoading={isTrendingDataLoading}
      isParameterDataLoading={isParameterDataLoading}
      filterComponent={
        <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
          <VerticalDateFilter
            startDateSelection={startDateSelection}
            setStartDateSelection={setStartDateSelection}
            endDateSelection={endDateSelection}
            setEndDateSelection={setEndDateSelection}
            searchLoading={isTrendingDataLoading}
            downloadLoading={downloadLoading}
            onFilterClick={onFilterClick}
            onExportClick={onExportClick}
            minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
            maxDate={dayjs()}
          />
          <span style={{ fontWeight: "bold" }}>Parameter Lists</span>
          <DeviceParameterFilter
            treeData={treeData}
            checkedKeys={deviceParameterFiltercheckedKeys}
            setCheckedKeys={setDeviceParameterFilterCheckedKeys}
          />
        </div>
      }
    />
  );
}
